import { omit } from 'lodash';
import { type IFramework } from '@/framework';
import { type ILayerProps, type ITextLayerProps } from '@/version-types';

/**
 * 更新舞台中的目标图层
 * @param framework - 框架实例
 * @param layer - 图层
 */
export const updateLayer = (
    framework: IFramework,
    layer: ILayerProps,
    options: { activate?: boolean } = { activate: true },
) => {
    const { activate } = options;

    framework.stage.addOrUpdate(layer);
    if (activate) framework.operate.setActiveLayer(layer.id);
};

/**
 * 写入历史记录
 * @param framework - 框架实例
 * @param prevLayer - 更新前的图层
 * @param nextLayer - 更新后的图层
 * @param funcID - 功能 ID，主要用于埋点统计
 */
export const pushHistory = (framework: IFramework, prevLayer: ILayerProps, nextLayer: ILayerProps, funcID?: string) => {
    const prev = omit(prevLayer, '__isOnTextEditing');
    const next = omit(nextLayer, '__isOnTextEditing');
    framework.history.write(prev, next, funcID);
};

/**
 * 更新组图层并写入历史记录
 * @param framework - 框架实例
 * @param groupLayer - 组图层
 * @param children - 子图层
 * @param funcID - 功能 ID，主要用于埋点统计
 * @returns
 */
export const updateGroupLayer = (
    framework: IFramework,
    groupLayer: ILayerProps,
    children: ILayerProps[],
    options: { funcID?: string; update?: boolean; writeHistory?: boolean },
) => {
    if (children.length === 0) return groupLayer;

    const { funcID = '', update = true, writeHistory = true } = options;

    const prevChildren = groupLayer.children;

    const nextChildren = prevChildren.map((prev, idx) => {
        const next = children[idx];
        return next ? next : prev;
    });

    let nextGroupLayer: ILayerProps = { ...groupLayer };

    if (nextChildren.length > 0) {
        nextGroupLayer = { ...groupLayer, children: nextChildren };
        if (update) updateLayer(framework, nextGroupLayer);
        if (writeHistory) pushHistory(framework, groupLayer, nextGroupLayer, funcID);
    }

    return nextGroupLayer;
};

/**
 * 获取图层字体 id 列表
 * @param framework - 框架实例
 * @param layer - 图层
 * @returns
 */
export const getLayerFontFamilyIds = (framework: IFramework, layer: ILayerProps) => {
    const layerData = layer.data as ITextLayerProps;

    // 找到图层中主文本所使用到的字体
    const fontFamilies: string[] = [layerData.fontFamily];
    if (layerData.textContents && layerData.textContents.length > 0) {
        layerData.textContents.forEach((item) => {
            const scopedFontFamily = item.fontFamily;
            // ATTENTION: scopedFontFamily 可能为空
            if (scopedFontFamily && !fontFamilies.includes(scopedFontFamily)) fontFamilies.push(scopedFontFamily);
        });
    }

    const fontFamilyIds: string[] = [];

    // 收集图层中主文本所使用到的字体 id
    const editorFonts = framework.getFontList();
    fontFamilies.forEach((fontFamily) => {
        const font = editorFonts.find((item) => item['font_name'] === fontFamily);
        if (font) fontFamilyIds.push(String(font.id));
    });

    // 收集图层中列表序号所使用到的字体 id
    const listStyleMaterial = layerData.listStyleMaterial;
    if (listStyleMaterial && listStyleMaterial.fontId) {
        fontFamilyIds.push(listStyleMaterial.fontId);
    }

    return fontFamilyIds;
};
